import { navigate } from 'gatsby';
import { getStateCode } from '~state/getStateField';
import { store } from '~state/ReduxWrapper';

import redirectToFutureVoter from '~components/Register/redirectToFuturevoter';
import {
  ineligibleStates,
  registrationNotRequired,
} from '~components/Register/specialCases';
import {
  sendRegistrationForm,
  completeRegisterIneligible,
} from './postFinishEvents';

/**
 * This navigates to the appropriate "second step" of Register -- the screen
 * the user should see after submitting their basic biographical information.
 *
 * skipOVR: if we've already told the user about OVR, and they want to print and
 *   mail anyway, pass true
 *
 * replace: When routing to the next URL, replace the current history entry
 *   instead of pushing a new entry onto the browser history.
 */
export default function navigateToSecondStep({
  skipOVR = false,
  replace = false,
} = {}) {
  const state = getStateCode();

  const { is_18_or_over: is18OrOver, ovr_link: ovrLink } =
    store.getState().registerFields;
  const { phone } = store.getState().person;

  if (!is18OrOver && phone) {
    // If they are < 18 and we already have their phone -> send them straight into the FutureVoter flow
    redirectToFutureVoter();
  } else if (registrationNotRequired.includes(state)) {
    // If registration is not required (ND) -> Tell them this right away — even if they are under-18-with-no-phone
    // (we don't want to make them bother entering phone number just to find out they don't have to register)
    completeRegisterIneligible('register', 'state');
  } else if (!is18OrOver) {
    // if they are < 18 and they haven't provided phone -> we want to ask for phone before sending them to FutureVoter
    navigate('/tools/register/ineligible-age/', { replace });
  } else if (ineligibleStates.includes(state)) {
    // If they are >= 18 but in a state that is not eligible for our help for any other reason (NH) -> send them special messaging for this
    completeRegisterIneligible('register', 'state');
  } else if (!skipOVR && ovrLink) {
    // If they are eligible and an OVR option is available (and they haven't already opted not to use OVR)
    // -> Present them with the option to register through OVR
    navigate('/tools/register/proceed-with-state/', { replace });
  } else {
    // Any other case where they are eligible to register
    // (no special state circumstances, OVR is either unavailable or was skipped) -> Tell them we are sending them a PDF
    sendRegistrationForm();
  }
}

/**
 * This handles the result of POSTing a new Register record to the backend. It
 * stores the appropriate fields of the result in Redux, then calls
 * navigateToSecondStep() to navigate the user to the next step of the
 * process.
 */
export function handleRegisterPostResult(
  postValues,
  postResult,
  referringTool = null
) {
  const registerStateUpdate = {
    uuid: postResult.uuid || '',
    action_id: postResult.action_id || '',
    sms_opt_in_subscriber: postValues.sms_opt_in_subscriber,
    sms_opt_in_voteamerica: postValues.sms_opt_in_voteamerica,
    ovr_link: postResult.ovr_link,
    is_18_or_over: postResult.is_18_or_over,
    region: postResult.matched_region,
    formRequested: false,
    referring_tool: referringTool,
  };

  store.dispatch({
    type: 'REGISTER_UPDATE_FIELDS',
    registerFields: registerStateUpdate,
  });

  navigateToSecondStep();
}
